import { useEffect, useState } from "react";
import { Link } from "easy-page-router/react";
import { useSSRHook } from "ssr-hook";
import Icon from "@mdi/react";
import { mdiPlus } from "@mdi/js";

import { useHead } from "../../services/common";
import { Footer, Header } from "../../Layout";
import { Info, Loading, Page, ReloadLine, ShareButton } from "../../components";
import { useLang } from "../../services/lang";
import { api, handleError, useHandleError } from "../../services/api";
import { getLocalId } from "easy-analytics/client";
import { useLocalHistory } from "../../services/localStorage";

import { WhoTable } from "./components/WhoTable";
import { WhoVoting } from "./components/WhoVoting";
import { WhoClient, VoteFull } from "../../../types";

export default function WhoPage({ id }: { id: string }) {
    const { dict } = useLang();

    useLocalHistory("who", id);

    const [who, error, isLoading, reload] = useSSRHook<WhoClient>(`/api/who?whoId=${encodeURIComponent(id)}`);
    useHandleError(error);

    const [isVoteMode, setIsVoteMode] = useState(false);
    const [vote, setVote] = useState<null | VoteFull>(null);

    const [message, setMessage] = useState("");

    useEffect(() => {
        if (vote !== null) return;

        api.getWhoVote({ whoId: id, userId: getLocalId() }).then(([vote, error]) => {
            if (error) {
                handleError(error);
                setVote(null);
                return;
            } else {
                setVote(vote);
            }
        });
    }, [id, vote]);

    const title = dict.seo.who.getTitle(who?.title);
    const description = dict.seo.who.getDescription(who?.title, who?.description);

    useHead({
        title,
        description,
    });

    function handleChangeVote(message: string) {
        setMessage(message);
        setIsVoteMode(false);
        reload();
        setVote(null);
    }

    return <Page theme={who?.theme} fixed={<>
        <Header title={dict.who} link={`/${dict.urlWho}`}>
            <Link href={`/${dict.urlWho}/${dict.urlNew}`}><Icon path={mdiPlus} size={1} />{dict.newWho}</Link>
            <ShareButton title={title} description={description} />
        </Header>
        <Footer />
        <ReloadLine stopReload={isVoteMode} value={who} error={error} isLoading={isLoading} onReload={reload} />
    </>}>
        <div className={`full-page who theme-${who?.theme || ""}`}>
            {isLoading && <Loading />}
            {error && <p className="error">{dict.whosError}</p>}
            {who && <>
                <h1>{who.title}</h1>
                {who.description && <p className="description width-max pre-line">{who.description}</p>}
                <Info {...who} />

                {!isVoteMode && <>
                    <button className="center" onClick={() => setIsVoteMode(true)}>
                        {vote === null ? dict.whoVote : dict.whoChangeVote}
                    </button>
                    {message && <p className="info">{message}</p>}
                    <WhoTable who={who} />
                </>}

                {isVoteMode && <WhoVoting who={who} vote={vote} onChange={handleChangeVote} />}
            </>}
        </div>
    </Page>;
}
